import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';

import { OnboardingStyle } from '../onboarding/onboarding-style';
import usePushDataLayerEvent from '../../../utils/google-analytics.util';
import { hideModal, showModal } from '../../../store/slices/generalSlice';
import { RootState } from '../../../store/rootReducer';
import useManageStorage from '../../../hooks/useManageStorage/useManageStorage';
import useAssistance from '../../../hooks/useAssistance/useAssistance';
import { AuthHeader, ContentLayout } from '../../../global-styles/layout';
import { BARRAVAS_URL } from '../../../constants/env';
import { Heading2Text } from '../../../components/typography/typography';
import ModalContainer from '../../../components/common/modal-container/modal-container';
import { images } from '../../../assets/images/images';
import { findSurvey } from '../../../api/survey/survey';
import { CompletedParams } from './completed-types';
import { CompletedStyles } from './completed-style';

const Completed = () => {
  const { t } = useTranslation(['common', 'completed']);
  const [surveyExists, setSurveyExists] = useState(true);
  const navigate = useNavigate();
  const { pushDataLayerEvent } = usePushDataLayerEvent();
  const { writeToStorage } = useManageStorage();
  const dispatch = useDispatch();
  const modal = useSelector((state: RootState) => state.general.modal);
  const [postponedSurvey, setPostponedSurvey] = useState(false);
  const [vasUrl, setVasUrl] = useState<string>('');
  const { file, intervention } = useParams<CompletedParams>();
  const { requestIntervention } = useAssistance({ intervention });
  const { data } = requestIntervention;

  const media = useMemo(() => {
    return data?.tareaMedio ?? false;
  }, [data]);

  const vas = useMemo(() => {
    return data?.entidad === '00000025';
  }, [data]);

  useEffect(() => {
    const checkSurvey = async () => {
      if (intervention) {
        const survey = await findSurvey(intervention);
        setSurveyExists(survey.codigo === '1');

        if (file) {
          setVasUrl(`${BARRAVAS_URL}?user=${file}mb`);
        }
      }
    };
    checkSurvey();
  }, [file, intervention]);

  useEffect(() => {
    if (!surveyExists) {
      dispatch(showModal({ name: 'completed-quiz' }));
    } else {
      dispatch(hideModal());
    }
  }, [dispatch, surveyExists]);

  const handleDoSurvey = async () => {
    if (!file) return;
    pushDataLayerEvent('empezar_encuesta', null, 'finalizar-servicio');
    await writeToStorage('surveyFromCompleted', true);
    navigate(`/survey/${file}/${intervention}`);
  };

  const handlePostponeSurvey = () => {
    pushDataLayerEvent('posponer_encuesta', null, 'finalizar-servicio');
    dispatch(hideModal());
    setPostponedSurvey(true);
  };

  useEffect(() => {
    if (postponedSurvey) {
      const interval = setInterval(() => {
        dispatch(showModal({ name: 'completed-quiz' }));
        setPostponedSurvey(false);
      }, 60000 * 10);

      return () => clearInterval(interval);
    }
  }, [dispatch, postponedSurvey]);

  return (
    <ContentLayout>
      <CompletedStyles>
        <OnboardingStyle>
          <div className="onboarding-top">
            <AuthHeader>
              <div className="auth-header-title">
                <Heading2Text as="h1" weight={600}>
                  {t('completed:title')}
                </Heading2Text>
              </div>
              <div className="auth-header-title">
                <Heading2Text as="h2" weight={600}>
                  {t('completed:text')}
                </Heading2Text>
              </div>
            </AuthHeader>
            <div className="completed-img">
              <img src={images.cars} alt="" />
            </div>
            {media && <div className="media-text">{t('completed:media')}</div>}
            {vas && (
              <>
                <div className="remember-text">{t('completed:remember')}</div>
                <Button component="a" href={vasUrl} target="_blank" className="vas-button">
                  {t('completed:button')}
                </Button>
              </>
            )}
          </div>
        </OnboardingStyle>
      </CompletedStyles>
      <ModalContainer
        buttons={[
          {
            className: 'primary',
            onClick: handleDoSurvey,
            label: t('aia:confirmation.success.do-survey'),
          },
          {
            className: 'secondary',
            onClick: handlePostponeSurvey,
            label: t('aia:confirmation.success.to-do-later'),
          },
        ]}
        active={modal.name === 'completed-quiz'}
        onClose={() => dispatch(hideModal())}
        title={t('aia:confirmation.success.quiz-modal.title')}
        text={t('aia:confirmation.success.quiz-modal.text')}
        dataCy="reset-form-modal"
      ></ModalContainer>
    </ContentLayout>
  );
};

export default Completed;
